import queryString from 'query-string';
import {
	PROMPT_SECUREID,
	SHOP_ITEM_FOR_DEPOSIT,
	UPDATE_PAYMENT_METHODS,
	INIT_SPEC,
	HYDRATED_DETAILS
} from '@comeon/actions-public';
import { selectSwishQRCodeFlow } from '~/components/brand/ducks/selectors.js';
import { skipAmountSuggestion } from '~/components/payment/deposit-beta/utils';
import {
	getFeesAndLimits,
	createBonusParams,
	validateSpec,
	getPAGdata,
	wrappedRequestApi
} from '../../../../ducks/index.js';
import { getSwishAppRedirectUrl } from '../../../utils/swish-app-redirect-util.js';
import { twoOrNoDecimals } from '../../../../deposit/data/string-num-clean.js';

/**
 * [allowsMethod Some bonuses are restricted to certain paymentmethods.
 * When entering deposit-flow from 'shop' this information comes as either
 * a list of id's, or a string value of 'ALL'. This differs
 * from the analyze of 'paymentMethodIdsStr' in ducks/bonuses/index.js
 *  allow certain :  "1,44,53" TODO confirm this
 *  allow all: 'ALL' or "" (empty)]
 * @param  {[type]} mId   [description]
 * @param  {[string]} idStr [ Comma-separated list of numbers or 'ALL' or empty]
 * @return {[boolean]}       [is or is not allowed for selected method]
 */
function allowsMethod(mId, idStr) {
	// when no rules are specified the method is allowed
	const emptyIdString = !idStr || !(idStr.length && idStr.length > 0);
	if (emptyIdString || idStr === 'ALL') return true;
	const hasIdInString = !!idStr.split(',').find(value => parseInt(value, 10) === parseInt(mId, 10));
	return hasIdInString;
}

// function getSelectedWelcomePackagePaymentMethodIdsStr(bonusState) {
// 	const result = bonusState.availableWelcomeBonusPackages.filter(obj => {
// 		return obj.id === bonusState.selectedWelcomePackageId;
// 	});

// 	return result ? result[0].paymentMethodIdsStr : null;
// }

/**
 * We check if the bonus selected is allowed with the currently selected
 * payment method. We need to consider that the user might not be selecting
 * a bonus from the shop, but also from the bonus carousel when making a deposit
 * or registration. If the method is not allowed we will enforce the step
 * shopbonusnotallowed which will trigger a modal to pop-up in <DepositFlow />.
 */
function shopItemAllowedByMethod(mId) {
	return (dispatch, getState) => {
		const { shopItem } = getState().payment;
		const bonusState = getState().bonuses;
		const bonusId = bonusState && bonusState.bonusId && bonusState.bonusId;
		const availableDepositOffers = bonusState && bonusState.availableDepositOffers;
		const currentBonus = availableDepositOffers ? availableDepositOffers.find(bonus => bonus.id === bonusId) : null;
		const paymentMethodIdsStr =
			(shopItem && shopItem.bonusPaymentMethods) || (currentBonus && currentBonus.paymentMethodIdsStr);
		// const bonusAddOnId = shopItem && shopItem.bonusAddOnId;
		// const addOnOptionsId = shopItem && shopItem.addOnOptionsId;
		const allowedForMethod = shopItem ? allowsMethod(mId, paymentMethodIdsStr) : '';
		const payload = shopItem ? { allowedForMethod } : '';
		// baid: payload.bonusAddOnId,
		// oid: payload.addOnOptionsId
		const promptShopBonusNotAllowed = shopItem && !allowedForMethod ? 'shopbonusnotallowed' : '';
		dispatch({
			type: PROMPT_SECUREID,
			payload: promptShopBonusNotAllowed
		});
		return dispatch({
			type: SHOP_ITEM_FOR_DEPOSIT,
			payload
		});
	};
}

// function shopItemAllowedByMethod(mId) {
// 	return (dispatch, getState) => {
// 		const { shopItem } = getState().payment;
// 		const bonusState = getState().bonuses;
// 		const bonusId = bonusState && bonusState.bonusId && bonusState.bonusId;
// 		const availableDepositOffers = bonusState && bonusState.availableDepositOffers;
// 		const currentBonus = availableDepositOffers ? availableDepositOffers.find(bonus => bonus.id === bonusId) : null;
// 		const paymentMethodIdsStr =
// 			(shopItem && shopItem.bonusPaymentMethods) ||
// 			(currentBonus && currentBonus.paymentMethodIdsStr) ||
// 			(bonusState.selectedWelcomePackageId && getSelectedWelcomePackagePaymentMethodIdsStr(bonusState));
// 		// const bonusAddOnId = shopItem && shopItem.bonusAddOnId;
// 		// const addOnOptionsId = shopItem && shopItem.addOnOptionsId;
// 		const allowedForMethod = paymentMethodIdsStr ? allowsMethod(mId, paymentMethodIdsStr) : '';
// 		const payload = paymentMethodIdsStr ? { allowedForMethod } : '';
// 		// baid: payload.bonusAddOnId,
// 		// oid: payload.addOnOptionsId
// 		const promptShopBonusNotAllowed = paymentMethodIdsStr && !allowedForMethod ? 'shopbonusnotallowed' : '';
// 		dispatch({
// 			type: PROMPT_SECUREID,
// 			payload: promptShopBonusNotAllowed
// 		});
// 		return dispatch({
// 			type: SHOP_ITEM_FOR_DEPOSIT,
// 			payload
// 		});
// 	};
// }

/**
 * accountToSpec: This transfers historical data to the format used for a
 * new deposit request. Each payment method gets preset information with
 * slightly different keys than used in deposit request.
 * @param {*} paymentMethodId
 * @param {*} type
 * @param {*} acc
 * @param {*} isMobileDevice
 */
function accountToSpec(paymentMethodId, type, acc, isMobileDevice, lastUsedBankCode, swishQRCodeFlow, playerId) {
	const def = {
		am: acc && acc.amount,
		cu: acc && acc.cu,
		paymentMethodId,
		isMobileDevice
	};
	const creditCardSpecs = {
		cid: acc && acc.id,
		cc: null,
		co: acc && acc.co,
		cn: acc && acc.cardNo,
		vy: acc && acc.vy,
		vm: acc && acc.vm,
		utimestamp: null
	};
	const specific = {
		'CREDIT-CARD': { ...creditCardSpecs },
		'DIRECTLY-ROUTED-CREDIT-CARD': { ...creditCardSpecs },
		'ASTROPAY-CREDIT-CARD': { ...creditCardSpecs },
		NETELLER: {
			externalid: acc && acc.externalid,
			id: null
		},
		ECOBANQ: {
			externalid: acc && acc.externalid,
			pwd: null
		},
		EZEE_WALLET: {
			externalid: acc && acc.externalid,
			pwd: null
		},
		'EMAIL-CURRENCIES': { em: acc && acc.email },
		EMAIL: { em: acc && acc.email },
		TSI: { externalid: acc && acc.externalid },
		VOUCHER: { externalid: acc && acc.externalid },
		'DOTPAY-BLIK': { blik: null },
		'TPAY-BLIK': { blik: null },
		PAYMERO_UPI: { externalid: acc && acc.externalid },
		PAYMERO_NET_BANKING: { selectedBank: lastUsedBankCode },
		PAYMERO_WALLET: { selectedBank: lastUsedBankCode },
		VENUSPOINT_DEPOSIT: {
			externalid: acc && acc.externalid,
			playerIdEncrypted: playerId,
			pwd: null
		},
		SWISH: { swishMobileFlow: swishQRCodeFlow, swishReturnPath: getSwishAppRedirectUrl() },
		AKONTO_PAY_NET_BANKING: { selectedBank: lastUsedBankCode },
		ZOTAPAY_NET_BANKING: { selectedBank: lastUsedBankCode },
		SAFEXPAY_NETBANKING: { selectedBank: lastUsedBankCode },
		PHONE: { countryCode: acc && acc.countryCode, phoneNumber: acc && acc.phoneNumber },
		MUCHBETTER: { countryCode: acc && acc.countryCode, phoneNumber: acc && acc.phoneNumber }
	};
	return { ...def, ...specific[type] };
}

/**
 * [updateDetails when a new method or account is selected, the amount and currency
 is preset depending on the userhistory.
 If the user has typed an amount or confirmed a preset value (which
 is done by making a failed submit and then select another method) this
 value is prioritized and always used as preset.]
 * @param  {object} accountIdx
 */
function updateDetails(accountIdx) {
	return async (dispatch, getState) => {
		const state = getState();
		const statePayment = state.payment;
		const { isDesktop } = state.brand;
		const { id: playerId } = state.account;
		const swishQRCodeFlow = selectSwishQRCodeFlow(state);
		const {
			status: { methodId, selectedMethodDetails },
			shopItem,
			paymentMethods
		} = statePayment;
		const bonusId = shopItem && shopItem.allowedForMethod ? shopItem.bonusId : '';
		const account = statePayment.methodDetails.accounts && statePayment.methodDetails.accounts[accountIdx];
		const { lastUsedBankCode } = statePayment.methodDetails;
		/* currencies are copied in two steps: first a shallow copy of the
		 array, and then an additional copy of each index that contains an object. */
		const currencies = statePayment.methodDetails.currencies && [...statePayment.methodDetails.currencies];
		/* from the list of currencies, one is marked as 'selected' */
		const currenciesSelected =
			currencies && currencies.length && currencies[0].value
				? currencies.filter(item => item.selected)[0].value
				: null;
		/* the properties from the accounthistory is matched to the submit-data-structure */
		/* some values might be empty, create keys for these too */
		const isMobileDevice = !isDesktop;
		const presetSpec = accountToSpec(
			statePayment.status.methodId,
			statePayment.status.formTemplateCode,
			account,
			isMobileDevice,
			lastUsedBankCode,
			swishQRCodeFlow,
			playerId
		);
		const PAGdata = getPAGdata(window.location.search);
		const amountInputPAG = PAGdata && JSON.parse(PAGdata).amount;
		const { isApplePay } = paymentMethods[methodId] ?? {};
		const cu =
			(isApplePay && statePayment.playerCurrency) ||
			statePayment.status.currencyuserinput ||
			(account && account.cu) ||
			currenciesSelected ||
			statePayment.playerCurrency;
		const isSiru = parseInt(methodId, 10) === 47;
		// TODO: improve structure somehow: repetitive statements follows...
		if (isSiru) {
			// do a fetch for feesAndLimits to get a set of default deposit amounts.
			// const feesAndLimits = await
			// 		const defaultUrl =
			const feesAndLimitsUrl = '/PaymentFormDataService/getDepositFeesAndLimits/forMobile';
			const siruEmptyAmount = 1;
			const params = {
				paymentMethodId: 47,
				amount: siruEmptyAmount,
				isMobileDevice: true,
				isInstantDeposit: false,
				currency: cu,
				bonusId
			};
			dispatch({
				type: INIT_SPEC,
				payload: {
					spec: {
						...presetSpec,
						am: 'siruEmptyAmount',
						cu
					},
					status: {
						accountIdx,
						presetAmount: 'siruEmptyAmount'
					}
				}
			});
			const response = await wrappedRequestApi(
				{
					endpoint: feesAndLimitsUrl,
					payload: queryString.stringify(params),
					method: 'POST',
					urlencodedContentType: true
				},
				dispatch
			);
			const { PARAM_AMOUNT1 } = response.defaultDepositAmounts;
			const am = twoOrNoDecimals(PARAM_AMOUNT1);
			dispatch({
				type: INIT_SPEC,
				payload: {
					spec: {
						...presetSpec,
						am,
						cu,
						...(bonusId
							? {
									bonusId,
									bid: bonusId
								}
							: {})
					},
					status: {
						accountIdx,
						presetAmount: am
					}
				}
			});
			dispatch(getFeesAndLimits());
			return dispatch(validateSpec());
		}
		const skipSuggestedAmount = skipAmountSuggestion();
		const suggestedAmount = skipSuggestedAmount
			? null
			: statePayment.lastUsedMethodDetails?.suggestedIncreasedAmount;
		// IF NOT SIRU:
		const prioritizedAmount =
			statePayment.status.amountuserinput ||
			amountInputPAG ||
			suggestedAmount ||
			account?.amount ||
			statePayment.lastUsedMethodDetails.lastAmount ||
			statePayment.status.presetAmount;
		const am = twoOrNoDecimals(prioritizedAmount);
		/* this amount can be formatted like "2530.0" and
		shall be presented either like "2530.00" or "2530".
		*/

		/* create read-only object: 'details' */
		/* 'spec' will reflect what will be used in a possible submit */

		const suggestedAmountForCurrency = state.brand?.suggestedAmounts?.[cu]?.split(',') ?? [];
		const finalAmount = selectedMethodDetails?.isAmountRestricted
			? suggestedAmountForCurrency.includes(am)
				? am
				: '0'
			: am;

		dispatch({
			type: INIT_SPEC,
			payload: {
				spec: {
					...presetSpec,
					am: finalAmount,
					cu,
					...(bonusId
						? {
								bonusId,
								bid: bonusId
							}
						: {})
				},
				status: {
					accountIdx,
					presetAmount: am
				}
			}
		});
		dispatch(getFeesAndLimits());
		return dispatch(validateSpec());
	};
}

function getPaymentMethodInitialData() {
	return async (dispatch, getState) => {
		const statePayment = getState().payment;
		const { isDesktop } = getState().brand;
		const {
			status: { methodId }
		} = statePayment;
		const { formTemplateCode, formTemplateUrlParams } = statePayment.paymentMethods[methodId];
		const theUrl = statePayment.formTemplateTypes[formTemplateCode];
		let payload = null;
		if (theUrl) {
			dispatch({
				type: HYDRATED_DETAILS,
				payload: { pending: true }
			});
			// the formTemplateUrlParams is already urlencoded
			payload = await wrappedRequestApi(
				{
					endpoint: theUrl,
					payload: `isMobile=${!isDesktop}${formTemplateUrlParams}`,
					method: 'POST',
					urlencodedContentType: true
				},
				dispatch
			);
		}
		// Moving the 'initDetails' function into this one, and creating a
		// longer function, with purpose to see the scope better. The 'initDetails'
		// function is only used byt this function.
		const lastUsedAccountIdx = payload ? payload.hasAccounts && payload.lastUsedAccountIdx : null;
		const accountIdx = parseInt(lastUsedAccountIdx, 10);

		dispatch({
			type: HYDRATED_DETAILS,
			payload: { ...payload, pending: false }
		});
		return dispatch(updateDetails(accountIdx));
	};
}

function selectPaymentMethod(methodId, autoskip = true) {
	return async (dispatch, getState) => {
		const statePayment = getState().payment;
		const bonusParams = statePayment.spec ? createBonusParams(statePayment.spec) : {};
		const mId = parseInt(methodId, 10);
		const { formTemplateCode, methodText } = statePayment.paymentMethods[mId];
		const { amountuserinput } = statePayment.status;
		const selectedMethodDetails = statePayment?.paymentMethods[mId];
		const selectedMethodAccountNo = selectedMethodDetails.extraParameters?.accountNumber;

		// if seleceted selectedMethod Account/Bank Details are available as part of 'extraParameters' then we hide the method name(eg:TRUSTLY) on amounts page
		const selectedMethodParams = selectedMethodAccountNo
			? {
					externalid: selectedMethodAccountNo,
					selectedBank: selectedMethodDetails.extraParameters?.bankName,
					hideMethodName: true
				}
			: {};

		if (formTemplateCode === 'VIRTUAL-CREDIT-CARD' || formTemplateCode === 'PEER-TO-PEER') {
			// FIXME
			// eslint-disable-next-line no-param-reassign
			autoskip = false;
		}

		dispatch(shopItemAllowedByMethod(mId));

		dispatch({
			type: UPDATE_PAYMENT_METHODS,
			payload: {
				status: {
					autoskip,
					methodId: mId,
					methodText,
					formTemplateCode,
					selectedMethodDetails,
					amountuserinput,
					presetAmount: amountuserinput
				},
				spec: { ...bonusParams, ...selectedMethodParams }
			}
		});
		return dispatch(getPaymentMethodInitialData());
	};
}

export { selectPaymentMethod, updateDetails };
